import SingleLineDT from "../../../assets/icons/SingleLineDT";
import MultiLineDT from "../../../assets/icons/MultiLineDT";
import NumberDT from "../../../assets/icons/NumberDT";
import DateTImeDT from "../../../assets/icons/DateTImeDT";
import FileUploadDT from "../../../assets/icons/FileUploadDT";
import SliderDT from "../../../assets/icons/SliderDT";
import VideoEmbedDT from "../../../assets/icons/VideoEmbedDT";
import AudioEmbedDT from "../../../assets/icons/AudioEmbedDT";
import RadioDT from "../../../assets/icons/RadioDT";
import MultiChoiceDT from "../../../assets/icons/MultiChoiceDT";
import CheckBoxDT from "../../../assets/icons/CheckBoxDT";

import React from "react";
/** ------------------------------------------------------------------ */
/**  1) All possible field data types                                 */
/** ------------------------------------------------------------------ */
export type FieldDataType =
  | "SINGLE_LINE"
  | "MULTI_LINE"
  | "NUMBERS"
  | "SELECT"
  | "RADIO"
  | "CHECKBOX"
  | "MULTIPLE_CHOICES"
  | "DATE_TIME"
  | "FILE_UPLOAD"
  | "SLIDER"
  | "VIDEO_EMBED"
  | "AUDIO_EMBED"
  | "DISPLAY_ONLY";

/** Type guard */
export const isFieldDataType = (value: unknown): value is FieldDataType =>
  typeof value === "string" &&
  [
    "SINGLE_LINE",
    "MULTI_LINE",
    "NUMBERS",
    "SELECT",
    "RADIO",
    "CHECKBOX",
    "MULTIPLE_CHOICES",
    "DATE_TIME",
    "FILE_UPLOAD",
    "SLIDER",
    "VIDEO_EMBED",
    "AUDIO_EMBED",
    "DISPLAY_ONLY"
  ].includes(value);

/** ------------------------------------------------------------------ */
/**  2) Category groupings (like before)                              */
/** ------------------------------------------------------------------ */

// Free-text input (placeholder, char limit, etc.)
const TEXT_FIELDS = new Set<FieldDataType>(["SINGLE_LINE", "MULTI_LINE"]);

// Numeric (range, separators, currency, etc.)
const NUMERIC_FIELDS = new Set<FieldDataType>(["NUMBERS"]);
// Choice-based input (dropdown, radio, checkbox, multiple choice)
const CHOICE_FIELDS = new Set<FieldDataType>([
  "SELECT",
  "RADIO",
  // "CHECKBOX",
  "MULTIPLE_CHOICES",
]);

// Uploads
const FILE_UPLOAD_FIELDS = new Set<FieldDataType>(["FILE_UPLOAD"]);

// Date/time
const DATE_FIELDS = new Set<FieldDataType>(["DATE_TIME"]);

// Range-style numeric input
const SLIDER_FIELDS = new Set<FieldDataType>(["SLIDER"]);

// Embeds
const MEDIA_EMBED_FIELDS = new Set<FieldDataType>(["VIDEO_EMBED", "AUDIO_EMBED"]);

/** ------------------------------------------------------------------ */
/**  3) UI helper functions                                            */
/** ------------------------------------------------------------------ */

export const showPlaceholderSettings = (type?: string): boolean =>
  isFieldDataType(type) && TEXT_FIELDS.has(type);

export const showCharacterLimitSettings = (type?: string): boolean =>
  isFieldDataType(type) && TEXT_FIELDS.has(type);

export const showRangeSettings = (type?: string): boolean =>
  isFieldDataType(type) &&
  (NUMERIC_FIELDS.has(type) || SLIDER_FIELDS.has(type));

export const showThousandSeparatorSettings = (type?: string): boolean =>
  isFieldDataType(type) && NUMERIC_FIELDS.has(type);

// Show Decimal Places setting for numbers (guard actual visibility via config in modal)
export const showDecimalPlacesSettings = (type?: string): boolean =>
  isFieldDataType(type) && type === "NUMBERS";

export const showCurrencySelectorSettings = (type?: string): boolean =>
  isFieldDataType(type) && NUMERIC_FIELDS.has(type);

export const showChoiceListSettings = (type?: string): boolean =>
  isFieldDataType(type) && CHOICE_FIELDS.has(type);

export const showBooleanSettings = (type?: string): boolean =>
  isFieldDataType(type) && type === "CHECKBOX";

export const showTimeFieldSettings = (type?: string): boolean =>
  isFieldDataType(type) && DATE_FIELDS.has(type);

export const showMonthYearFieldSettings = (type?: string): boolean =>
  isFieldDataType(type) && DATE_FIELDS.has(type);

export const showFileUploadSettings = (type?: string): boolean =>
  isFieldDataType(type) && FILE_UPLOAD_FIELDS.has(type);

export const showImageFieldSettings = (type?: string): boolean =>
  isFieldDataType(type) && FILE_UPLOAD_FIELDS.has(type); // adjust if you later split IMAGE_UPLOAD

export const showSliderFieldSettings = (type?: string): boolean =>
  isFieldDataType(type) && SLIDER_FIELDS.has(type);

export const showDescriptionSettings = (type?: string): boolean =>
  isFieldDataType(type) && !MEDIA_EMBED_FIELDS.has(type); // available for all textual / input types

export const showAudioVideoEmbedSettings = (type?: string): boolean =>
  isFieldDataType(type) && MEDIA_EMBED_FIELDS.has(type);

export const showMandatoryFieldSettings = (type?: string): boolean =>
  isFieldDataType(type) && type !== "VIDEO_EMBED" && type !== "AUDIO_EMBED";

export const showVisibilitySettings = (type?: string): boolean =>
  isFieldDataType(type);

/** Tooltip, description, etc. are safe for all UI fields */
export const showTooltipSettings = (type?: string): boolean =>
  isFieldDataType(type);



export const createNewField = async (
  fieldName: string,
  fieldDataType: string,
  fieldScope: string | number,
  config: any,
  accessKey: string,
  createFieldTemplate: any,
  setRefreshTrigger: React.Dispatch<React.SetStateAction<number>>,
  showToast: (
    title: string,
    message: string,
    type: "error" | "success",
    duration?: number
  ) => void,
  onSuccess?: () => void // 👈 new optional callback
) => {
  try {
    await createFieldTemplate({
      name: fieldName,
      dataType: fieldDataType,
      scopeId: typeof fieldScope === 'string' ? Number(fieldScope) : fieldScope,
      config,
      accessKey,
    }).unwrap();

    showToast(
      "Custom Field Created",
      "New field template created successfully",
      "success"
    );

    setRefreshTrigger((prev: number) => prev + 1);

    if (onSuccess) {
      onSuccess();
    }
  } catch (error: any) {
    const message =
      error?.data?.response?.errors ||
      error?.data?.response?.message ||
      error?.data?.message ||
      "Unknown error";

    showToast("Creation Failed", message, "error");
  }
};

/** ------------------------------------------------------------------ */
/**  4) DataType icons mapping and helper                            */
/** ------------------------------------------------------------------ */



/** A simple mapping from FieldDataType to an icon component */
export const DataTypeIcons: Record<FieldDataType, React.FC<any>> = {
  SINGLE_LINE: SingleLineDT,
  MULTI_LINE: MultiLineDT,
  NUMBERS: NumberDT,
  SELECT: MultiChoiceDT,
  RADIO: RadioDT,
  CHECKBOX: CheckBoxDT,
  MULTIPLE_CHOICES: MultiChoiceDT,
  DATE_TIME: DateTImeDT,
  FILE_UPLOAD: FileUploadDT,
  SLIDER: SliderDT,
  VIDEO_EMBED: VideoEmbedDT,
  AUDIO_EMBED: AudioEmbedDT,
  DISPLAY_ONLY: SingleLineDT,
};

/**
 * Return a React element for a given FieldDataType.
 * Props (size, color, className, onClick) are forwarded to the icon component.
 */
export const getIconForDataType = (
  type?: string,
  props?: { size?: number | string; color?: string; className?: string; onClick?: () => void }
) => {
  if (!isFieldDataType(type)) return React.createElement(SingleLineDT, props || {});
  const Icon = DataTypeIcons[type];
  if (!Icon) return React.createElement(SingleLineDT, props || {});
  // Use createElement because this file is .ts (not .tsx)
  return React.createElement(Icon, props || {});
};

export const moveItem = <T,>(array: T[], from: number, to: number) => {
  const newArray = [...array];
  const [removed] = newArray.splice(from, 1);
  newArray.splice(to, 0, removed);
  return newArray;
};